if (!customElements.get('cart-donation')) {
  customElements.define('cart-donation', class CartDonation extends HTMLElement {
    constructor() {
      super();

      this.config = JSON.parse(this.querySelector('[data-donation-config]').textContent);
      this.toggle = this.querySelector('[data-donation-toggle]');
      this.amounts = this.querySelector('[data-donation-amounts]');
      this.pills = Array.from(this.querySelectorAll('[data-donation-pill]'));
      this.customWrapper = this.querySelector('[data-donation-custom]');
      this.input = this.querySelector('[data-donation-input]');
      this.error = this.querySelector('[data-donation-error]');
      this.status = this.querySelector('[data-donation-status]');
      this.liveRegion = this.querySelector('[data-donation-live]');
      this.form = document.getElementById('cart');

      // Donation lines currently in the cart: [{ variantId, quantity, price }]
      this.lines = this.config.lines;
      this.state = this.stateFromLines();
      this.timer = null;
      this.queued = false;
      this.syncPromise = null;
      this.failed = false;
      // True when the page may no longer match the cart (a request failed and so did the re-read).
      this.stale = false;

      this.toggle.addEventListener('change', this.onToggle.bind(this));
      this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick.bind(this)));
      if (this.input) {
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.onInputChange();
        });
      }

      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onCheckoutClick = this.onCheckoutClick.bind(this);
    }

    connectedCallback() {
      document.addEventListener('cart:updated', this.onCartUpdated);
      document.addEventListener('click', this.onCheckoutClick);
      this.updateUI();

      if (this.initialised) return;
      this.initialised = true;

      // A donation never stays in the cart on its own, and odd states
      // (two donation lines, quantity above 1) collapse back to one line.
      if (this.lines.length && !this.config.hasOtherItems) {
        this.state.checked = false;
        this.updateUI();
        this.sync();
      } else if (this.needsNormalising) {
        this.sync();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('click', this.onCheckoutClick);
      clearTimeout(this.timer);
      this.timer = null;
    }

    defaultSelection() {
      const { variants, defaultPrice, unitVariantId } = this.config;
      const preferred = variants.find((variant) => variant.price === defaultPrice) || variants[0];
      if (preferred) return preferred.id;
      return unitVariantId ? 'custom' : null;
    }

    stateFromLines() {
      const { variants, unitVariantId } = this.config;
      const state = { checked: false, selection: this.defaultSelection(), custom: '' };
      this.needsNormalising = false;
      if (!this.lines.length) return state;

      state.checked = true;
      const [line] = this.lines;
      if (this.lines.length === 1 && line.variantId === unitVariantId) {
        state.selection = 'custom';
        state.custom = String(line.quantity);
      } else if (this.lines.length === 1 && line.quantity === 1 && variants.some((variant) => variant.id === line.variantId)) {
        state.selection = line.variantId;
      } else {
        const cents = this.lines.reduce((total, item) => total + item.price * item.quantity, 0);
        if (unitVariantId && cents % 100 === 0 && cents / 100 <= this.config.maxAmount) {
          state.selection = 'custom';
          state.custom = String(cents / 100);
        }
        this.needsNormalising = true;
      }
      return state;
    }

    // Whole rand amount entered under "Own amount", or null when it isn't valid.
    customAmount() {
      const value = this.state.custom.trim();
      if (!/^\d+$/.test(value)) return null;
      const amount = parseInt(value, 10);
      return amount >= 1 && amount <= this.config.maxAmount ? amount : null;
    }

    // The donation line the cart should hold for the current selection, or null for none.
    desiredLine() {
      if (!this.state.checked || this.state.selection === null) return null;
      if (this.state.selection === 'custom') {
        const amount = this.customAmount();
        return amount ? { variantId: this.config.unitVariantId, quantity: amount } : null;
      }
      return { variantId: this.state.selection, quantity: 1 };
    }

    matchesCart(desired) {
      if (!desired) return this.lines.length === 0;
      return this.lines.length === 1 && this.lines[0].variantId === desired.variantId && this.lines[0].quantity === desired.quantity;
    }

    onToggle() {
      this.state.checked = this.toggle.checked;
      this.updateUI();
      if (this.state.checked && this.state.selection === 'custom' && !this.customAmount()) this.input.focus();
      this.flush();
    }

    onPillClick(event) {
      const value = event.currentTarget.dataset.donationPill;
      this.state.selection = value === 'custom' ? 'custom' : Number(value);
      this.updateUI();
      if (value === 'custom') this.input.focus();
      this.flush();
    }

    onInput() {
      this.state.custom = this.input.value;
      this.showError(null);
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.timer = null;
        this.sync();
      }, 600);
    }

    onInputChange() {
      this.state.custom = this.input.value;
      this.validateCustom(false);
      this.flush();
    }

    // Shows the "Own amount" error when needed; returns true when the amount is usable.
    validateCustom(required) {
      const value = this.state.custom.trim();
      if (value === '' && !required) {
        this.showError(null);
        return false;
      }
      if (this.customAmount()) {
        this.showError(null);
        return true;
      }
      const overMax = /^\d+$/.test(value) && parseInt(value, 10) > this.config.maxAmount;
      this.showError(overMax ? this.config.strings.maxError : this.config.strings.amountError);
      return false;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
      if (message) {
        this.input.setAttribute('aria-invalid', 'true');
      } else {
        this.input.removeAttribute('aria-invalid');
      }
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }

    onCheckoutClick(event) {
      const button = event.target.closest('[name="checkout"]');
      if (!button || !this.form || button.form !== this.form) return;

      if (this.state.checked && this.state.selection === 'custom' && !this.validateCustom(true)) {
        event.preventDefault();
        this.input.focus();
        return;
      }

      // Let a pending donation change reach the cart before going to checkout.
      if (this.timer || this.syncPromise || this.stale) {
        event.preventDefault();
        this.flush().then(() => {
          if (!this.failed && !this.stale) button.click();
        });
      }
    }

    // Quantity changes and removals made through cart.js.
    onCartUpdated(event) {
      const cart = event.detail && event.detail.cart;
      if (!cart || !Array.isArray(cart.items)) return;

      this.setLines(cart);
      if (this.lines.length === cart.items.length) {
        this.state.checked = false;
        this.updateUI();
        if (this.lines.length) this.sync();
        return;
      }

      // The cart is the truth: follow it unless a change of ours is still on its way.
      if (!this.timer && !this.syncPromise && !this.matchesCart(this.desiredLine())) {
        this.state = this.stateFromLines();
        this.updateUI();
        if (this.needsNormalising) this.sync();
      }
    }

    setLines(cart) {
      this.lines = cart.items
        .filter((item) => item.product_id === this.config.productId)
        .map((item) => ({ variantId: item.variant_id, quantity: item.quantity, price: item.price }));
    }

    flush() {
      clearTimeout(this.timer);
      this.timer = null;
      return this.sync();
    }

    // The latest selection always wins; requests share one queue with cart.js.
    sync() {
      this.queued = true;
      if (!this.syncPromise) {
        this.syncPromise = (async () => {
          this.setLoading(true);
          while (this.queued) {
            this.queued = false;
            try {
              await this.push();
              this.failed = false;
              this.showStatus(null);
            } catch (error) {
              this.failed = !(await this.onPushError());
            }
          }
          this.setLoading(false);
          this.syncPromise = null;
        })();
      }
      return this.syncPromise;
    }

    push() {
      const result = (window.cartRequestQueue || Promise.resolve()).then(() => this.request());
      window.cartRequestQueue = result.catch(() => {});
      return result;
    }

    async request() {
      if (this.stale) await this.refresh();

      const desired = this.desiredLine();
      if (this.matchesCart(desired)) return;

      const updates = {};
      this.lines.forEach((line) => { updates[line.variantId] = 0; });
      if (desired) updates[desired.variantId] = desired.quantity;

      const body = JSON.stringify({
        updates,
        sections: this.sectionIds(),
        sections_url: window.location.pathname
      });

      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      const cart = await response.json();
      if (!response.ok || !Array.isArray(cart.items)) throw new Error(cart.description || cart.message);

      this.applyCart(cart);
    }

    // After a failed request the cart may or may not have changed, so read it again.
    // Returns true when the change turns out to have gone through.
    async onPushError() {
      this.queued = false;
      const desired = this.desiredLine();
      let applied = false;
      try {
        await this.refresh();
        applied = this.matchesCart(desired);
      } catch (error) {
        this.stale = true;
      }
      this.showStatus(applied ? null : this.config.strings.updateError);
      this.state = this.stateFromLines();
      this.updateUI();
      return applied;
    }

    async refresh() {
      const [cart, sections] = await Promise.all([
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json()),
        fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`).then((response) => response.json())
      ]);
      if (!Array.isArray(cart.items)) throw new Error('Cart could not be read');

      cart.sections = sections;
      this.stale = false;
      this.applyCart(cart);
    }

    sectionIds() {
      const cartItems = document.querySelector('cart-items');
      return cartItems ? cartItems.getSectionsToRender().map((section) => section.section) : [];
    }

    applyCart(cart) {
      this.setLines(cart);
      this.renderCart(cart);
      this.announce();
    }

    renderCart(cart) {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems || !cart.sections) return;

      const isEmpty = cart.item_count === 0;
      cartItems.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer').classList.toggle('is-empty', isEmpty);

      cartItems.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        const html = cart.sections[section.section];
        if (!container || !html) return;
        const elementToReplace = container.querySelector(section.selector) || container;
        elementToReplace.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
      });

      // Keeps cart.js from mistaking a donation change for a refused quantity change.
      cartItems.currentItemCount = cart.item_count;
    }

    // Reads the donation and total rows out for screen readers.
    announce() {
      if (!this.liveRegion) return;
      const rows = document.querySelectorAll('#main-cart-footer .cart-summary__row--donation, #main-cart-footer .cart-summary__row--total');
      this.liveRegion.textContent = Array.from(rows)
        .map((row) => row.textContent.replace(/\s+/g, ' ').trim())
        .join('. ');
    }

    setLoading(loading) {
      const footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('cart-donation-loading', loading);
    }

    updateUI() {
      const { checked, selection, custom } = this.state;
      this.toggle.checked = checked;
      this.amounts.hidden = !checked;
      this.pills.forEach((pill) => {
        const value = pill.dataset.donationPill;
        const pressed = value === 'custom' ? selection === 'custom' : Number(value) === selection;
        pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      });
      if (this.customWrapper) this.customWrapper.hidden = selection !== 'custom';
      if (this.input && this.input.value !== custom && document.activeElement !== this.input) this.input.value = custom;
    }
  });
}
